import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

/** Quick Milestones — the Overview page's hand-entered reporting list.
 *  SEPARATE from the Plan tab's milestones (Brett, 2026-09-14): every
 *  project starts blank. Yellow/red rows carry a go-to-green plan —
 *  what we'll do, who owns the recovery, when it lands. */
export interface QuickMilestone {
  id: number;
  project_id: string;
  title: string;
  owner: string | null;
  due_date: string | null;
  /** null = not assessed (hollow gray ring) — milestone hasn't started. */
  health: "green" | "yellow" | "red" | null;
  done: boolean;
  gtg_plan: string | null;
  gtg_owner: string | null;
  gtg_due: string | null;
  /** Drag-and-drop rank — midpoint float, same scheme as the board. */
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
}

const key = (projectId: string | null) => ["quick-milestones", projectId] as const;

export function useQuickMilestones(projectId: string | null) {
  return useQuery<QuickMilestone[]>({
    queryKey: key(projectId),
    queryFn: () => api.get(`/quick-milestones/${projectId}`).then((r) => r.data),
    enabled: !!projectId,
  });
}

export function useAddQuickMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      title: string;
      owner?: string | null;
      due_date?: string | null;
      health?: string;
    }) => api.post(`/quick-milestones/${projectId}`, payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(projectId) }),
  });
}

export function useUpdateQuickMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: Partial<QuickMilestone> & { id: number }) =>
      api.patch(`/quick-milestones/id/${id}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(projectId) }),
  });
}

export function useDeleteQuickMilestone(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/quick-milestones/id/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(projectId) }),
  });
}
